/**
 * <reinsurance-rotator> — shows one reassurance line at a time inside the PDP
 * sticky action zone, cycling through them.
 *
 * Matches the CD canvas (InfoColumnV6.dc.html): a single centred line, swapped on
 * an interval (3200ms there), each entrance animated with a short fade-up. The
 * container reserves its own min-height in CSS so the band never jumps as lines
 * of differing length replace each other.
 *
 * All lines are in the markup and only one is visible, so with JS off the first
 * line still renders — the feature degrades to a static reassurance line rather
 * than to nothing.
 */
class ReinsuranceRotator extends HTMLElement {
  connectedCallback() {
    // DIRECT children only. A line's text can itself contain a <span> — the despatch line injects
    // one (data-delivery-cutoff) and a money filter can emit one — and an unscoped
    // querySelectorAll('span') swept those nested spans in as if they were slides. The rotator
    // then "showed" a nested span while its parent line stayed hidden, producing a blank beat
    // in the cycle: the reported "a few seconds where we don't see anything after the last one".
    this.lines = Array.from(this.querySelectorAll(':scope > span'));
    if (this.lines.length < 2) return; // nothing to rotate

    this.index = 0;
    const interval = parseInt(this.dataset.interval, 10) || 3200;

    // Respect the user's motion preference: still rotate (it is information, not
    // decoration) but the CSS drops the transform, and a much slower cadence
    // keeps it from being a distraction.
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.timer = setInterval(() => this.advance(), reduced ? interval * 2 : interval);

    // Don't animate a panel nobody is looking at.
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  onVisibility = () => {
    if (document.hidden) clearInterval(this.timer);
    else if (this.lines && this.lines.length > 1) {
      clearInterval(this.timer);
      const interval = parseInt(this.dataset.interval, 10) || 3200;
      this.timer = setInterval(() => this.advance(), interval);
    }
  };

  advance() {
    this.lines[this.index].setAttribute('hidden', '');
    this.index = (this.index + 1) % this.lines.length;
    const next = this.lines[this.index];
    next.removeAttribute('hidden');
    // Restart the CSS entrance animation on the element being revealed.
    next.style.animation = 'none';
    void next.offsetWidth;
    next.style.animation = '';
  }
}

if (!customElements.get('reinsurance-rotator')) {
  customElements.define('reinsurance-rotator', ReinsuranceRotator);
}
